import { getConfig } from './config';

// Type definitions for gtag
declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}

// Placeholder ID used in dev/local environments
const PLACEHOLDER_TAG_ID = 'AW-00000000000';

// Get Google Tag ID from configuration
export const getGoogleTagId = (): string => {
  return getConfig('REACT_APP_GOOGLE_TAG_ID');
};

// Check if the Google Tag ID is valid (not empty and not a placeholder)
export const isValidGoogleTagId = (tagId: string): boolean => {
  return !!(tagId && tagId !== PLACEHOLDER_TAG_ID && tagId !== '');
};

// Initialize Google Tag
export const initializeGoogleTag = (): void => {
  const googleTagId = getGoogleTagId();
  
  if (!isValidGoogleTagId(googleTagId)) {
    return;
  }

  // Initialize dataLayer if it doesn't exist
  window.dataLayer = window.dataLayer || [];
  
  // Define gtag function
  window.gtag = function() {
    window.dataLayer.push(arguments);
  };

  // Set the initial timestamp
  window.gtag('js', new Date());

  // Configure the Google Tag
  window.gtag('config', googleTagId);

};

// Track a page view (for SPA navigation)
export const trackPageView = (path: string, title?: string): void => {
  const googleTagId = getGoogleTagId();
  
  if (!isValidGoogleTagId(googleTagId) || typeof window.gtag === 'undefined') {
    return;
  }

  window.gtag('config', googleTagId, {
    page_path: path,
    page_title: title
  });
};

// Track a conversion event
export const trackConversion = (conversionLabel: string, value?: number, currency?: string): void => {
  const googleTagId = getGoogleTagId();
  
  if (!isValidGoogleTagId(googleTagId) || typeof window.gtag === 'undefined') {
    return;
  }

  const conversionData: any = {
    'send_to': `${googleTagId}/${conversionLabel}`,
    'event_callback': () => {
    }
  };

  if (value !== undefined) {
    conversionData.value = value;
  }

  if (currency) {
    conversionData.currency = currency;
  }

  window.gtag('event', 'conversion', conversionData);
};

// Track a custom event
export const trackEvent = (action: string, category: string, label?: string, value?: number): void => {
  const googleTagId = getGoogleTagId();
  
  if (!isValidGoogleTagId(googleTagId) || typeof window.gtag === 'undefined') {
    return;
  }

  const eventData: any = {
    event_category: category,
  };

  if (label) {
    eventData.event_label = label;
  }

  if (value !== undefined) {
    eventData.value = value;
  }

  window.gtag('event', action, eventData);
};

// Track add to cart event
export const trackAddToCart = (itemId: string, itemName: string, value: number, currency: string = 'USD'): void => {
  const googleTagId = getGoogleTagId();
  
  if (!isValidGoogleTagId(googleTagId) || typeof window.gtag === 'undefined') {
    return;
  }

  window.gtag('event', 'add_to_cart', {
    currency: currency,
    value: value,
    items: [
      {
        item_id: itemId,
        item_name: itemName,
        price: value,
        quantity: 1
      }
    ]
  });
};

// Track begin checkout event
export const trackBeginCheckout = (value: number, currency: string = 'USD', items?: any[]): void => {
  const googleTagId = getGoogleTagId();
  
  if (!isValidGoogleTagId(googleTagId) || typeof window.gtag === 'undefined') {
    return;
  }

  const eventData: any = {
    currency: currency,
    value: value
  };

  if (items) {
    eventData.items = items;
  }

  window.gtag('event', 'begin_checkout', eventData);
};

// Track purchase event
export const trackPurchase = (
  transactionId: string,
  value: number,
  currency: string = 'USD',
  items?: any[]
): void => {
  const googleTagId = getGoogleTagId();
  
  if (!isValidGoogleTagId(googleTagId) || typeof window.gtag === 'undefined') {
    return;
  }

  const eventData: any = {
    transaction_id: transactionId,
    value: value,
    currency: currency
  };

  if (items) {
    eventData.items = items;
  }

  window.gtag('event', 'purchase', eventData);

  // Also track as a conversion if conversion label is configured
  // This would typically be configured based on your Google Ads setup
  // Example: trackConversion('YOUR_CONVERSION_LABEL', value, currency);
};

// Load Google Tag script dynamically
export const loadGoogleTagScript = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    const googleTagId = getGoogleTagId();
    
    if (!isValidGoogleTagId(googleTagId)) {
      resolve();
      return;
    }

    // Check if script already exists
    if (document.querySelector(`script[src*="googletagmanager.com/gtag/js?id=${googleTagId}"]`)) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${googleTagId}`;
    
    script.onload = () => {
      resolve();
    };
    
    script.onerror = () => {
      console.error('Failed to load Google Tag script');
      reject(new Error('Failed to load Google Tag script'));
    };
    
    document.head.appendChild(script);
  });
};